import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";
import useCallingAppEnabled from "./useCallingAppEnabled";

// Calling has its own dedicated single-instance service — falls back to the
// main API server only if REACT_APP_CALLING_URL is unset (e.g. not yet configured).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const CALLING_APP_DISABLED_MESSAGE = "Calling is turned off for this workspace right now.";

/**
 * Always a live fetch from Meta (via our backend), never a local cache —
 * Meta's own docs are explicit that no webhook fires when a temporary
 * permission simply expires, so anything cached here would silently go
 * stale exactly when it matters most. Re-fetches on mount / contactId change
 * and after a request is sent; no polling — permission state only matters at
 * the moment an agent actually looks at it or acts on it.
 */
export default function useCallPermission(contactId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Backend attaches a stable `code` (e.g. "CALLING_NOT_CONFIGURED") to a
  // known, actionable failure class alongside the human-readable `error`
  // string — kept separate so a consumer can render a distinct, calmer UI
  // state for "this integration needs attention" vs. a generic/transient
  // failure, without string-matching `error`'s text.
  const [errorCode, setErrorCode] = useState(null);
  const [requesting, setRequesting] = useState(false);
  // Same requestId-guard pattern CallLogsTable/CallsSectionBody already use
  // elsewhere in calling — without it, switching contacts quickly can let an
  // in-flight request for the PREVIOUS contact resolve after the next one
  // started and overwrite state with permission data for the wrong contact.
  const latestRequestIdRef = useRef(0);
  // Cheap, shared "is calling on at all" check (see useCallingAppEnabled's own
  // doc comment) — when it comes back false, skip the real GET entirely instead
  // of firing it and having the server deny/fail it. Defaults to true while
  // still loading, so this adds no delay for the common (enabled) case.
  const { enabled: callingAppEnabled } = useCallingAppEnabled();

  const fetchPermission = useCallback(async () => {
    if (!contactId) {
      latestRequestIdRef.current += 1; // invalidate any still-in-flight request for the previous contact
      setData(null);
      return;
    }
    if (!callingAppEnabled) {
      latestRequestIdRef.current += 1; // invalidate any still-in-flight request
      setError(CALLING_APP_DISABLED_MESSAGE);
      setErrorCode("CALLING_NOT_CONFIGURED"); // same code CallButton.jsx already renders calmly for a broken/unconfigured integration
      setData(null);
      setLoading(false);
      return;
    }
    const requestId = ++latestRequestIdRef.current;
    setLoading(true);
    setError(null);
    setErrorCode(null);
    try {
      const token = await getAuthToken();
      const res = await axios.get(`${SERVER_DOMAIN}/api/calling/permission`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { contactId },
      });
      if (requestId !== latestRequestIdRef.current) return; // a newer request has since started — this response is stale
      setData(res.data);
    } catch (err) {
      if (requestId !== latestRequestIdRef.current) return;
      setError(err.response?.data?.error || err.message || "Failed to load call permission");
      setErrorCode(err.response?.data?.code || null);
      setData(null);
    } finally {
      if (requestId === latestRequestIdRef.current) setLoading(false);
    }
  }, [contactId, callingAppEnabled]);

  useEffect(() => {
    fetchPermission();
  }, [fetchPermission]);

  const requestPermission = useCallback(
    async (reason) => {
      if (!contactId) return { success: false };
      setRequesting(true);
      try {
        const token = await getAuthToken();
        await axios.post(
          `${SERVER_DOMAIN}/api/calling/permission/request`,
          { contactId, reason },
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        await fetchPermission(); // usage counters change immediately even though the reply itself is async
        return { success: true };
      } catch (err) {
        return { success: false, error: err.response?.data?.error || err.message || "Failed to send request" };
      } finally {
        setRequesting(false);
      }
    },
    [contactId, fetchPermission]
  );

  return { data, loading, error, errorCode, requesting, refetch: fetchPermission, requestPermission };
}
